import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { findBestProductionHour } from './productionTimeHelpers';
import { AGGREGATED_MACHINE_STAT_ENDPOINT, DEFAULT_REFRESH_INTERVAL_MS } from './machineStatDataConstants';

const calculateEffectiveMinutes = (output) => {
  const first = output[0];
  const last = output[output.length - 1];
  return first ? (Date.parse(last.windowStart) - Date.parse(first.windowStart)) / 60_000 + (last.windowLength ?? 1) : 0;
};

export default function useProductionEffectiveTime({ dataset, datasets, rangeMode, from, to, excludeNoFlow = false }) {
  const includeBestHour = [dataset, ...(datasets ?? [])].some((item) => item?.highlightBestHour || item?.productionCards?.includes('bestHour'));
  const selectedEnabled =
    dataset?.allowGapExclusions ||
    dataset?.allowStopAnnotations ||
    dataset?.defaultExcludedTimes?.length ||
    dataset?.highlightBestHour ||
    dataset?.productionCards?.some((card) => ['runningMetres', 'productionTime', 'productionStart', 'productionEnd', 'effectiveTime', 'netRunningTime', 'bestHour'].includes(card));
  const areaIdentifiers = [
    ...new Set([
      ...(datasets ?? []).filter((item) => excludeNoFlow || item.defaultExcludedTimes?.length || item.allowStopAnnotations).map((item) => item.key),
      ...(selectedEnabled ? [dataset.key] : [])
    ])
  ]
    .sort()
    .join('|');
  const enabled = Boolean(areaIdentifiers);
  const [production, setProduction] = useState({ loading: true });
  const day = new Date(rangeMode === 'live' ? Date.now() : to);
  day.setHours(0, 0, 0, 0);
  const dayStart = Number.isNaN(day.getTime()) ? null : day.toISOString();
  const firstDay = new Date(rangeMode !== 'live' && from ? from : day);
  firstDay.setHours(0, 0, 0, 0);
  const fetchStart = dayStart && new Date(Math.min(firstDay.getTime(), day.getTime())).toISOString();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = dayStart === today.toISOString();

  useEffect(() => {
    setProduction({ loading: Boolean(enabled && dayStart) });
    if (!enabled || !dayStart) return undefined;

    let isCancelled = false;
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    dayEnd.setMilliseconds(-1);

    const fetchProduction = async () => {
      try {
        const results = await Promise.all(
          areaIdentifiers.split('|').map(async (areaIdentifier) => {
            const response = await axios.get(AGGREGATED_MACHINE_STAT_ENDPOINT, {
              params: { area_identifier: areaIdentifier, from: fetchStart, to: dayEnd.toISOString(), window_length: 1 }
            });
            const entries = response.data;
            const output = entries.filter((point) => point.aggregateSpeed > 0);
            const days = [];
            let cursor = 0;
            for (const currentDay = new Date(fetchStart); currentDay <= new Date(dayStart); currentDay.setDate(currentDay.getDate() + 1)) {
              const nextDay = new Date(currentDay);
              nextDay.setDate(nextDay.getDate() + 1);
              const dayOutput = [];
              while (cursor < output.length && Date.parse(output[cursor].windowStart) < nextDay.getTime()) dayOutput.push(output[cursor++]);
              days.push({
                dayStart: currentDay.toISOString(),
                productionStart: dayOutput[0]?.windowStart ?? null,
                productionEnd: dayOutput[dayOutput.length - 1]?.windowStart ?? null,
                effectiveMinutes: calculateEffectiveMinutes(dayOutput),
                bestHour: includeBestHour ? findBestProductionHour(entries, currentDay.getTime(), nextDay.getTime()) : null
              });
            }
            return [
              areaIdentifier,
              {
                days,
                effectiveMinutes: days.reduce((sum, entry) => sum + entry.effectiveMinutes, 0),
                entries,
                output
              }
            ];
          })
        );
        if (!isCancelled) setProduction({ loading: false, byArea: Object.fromEntries(results) });
      } catch {
        if (!isCancelled) setProduction((current) => ({ ...current, loading: false }));
      }
    };

    const timerId = isToday ? window.setInterval(fetchProduction, DEFAULT_REFRESH_INTERVAL_MS) : undefined;
    fetchProduction();
    return () => {
      isCancelled = true;
      window.clearInterval(timerId);
    };
  }, [areaIdentifiers, dayStart, enabled, fetchStart, includeBestHour, isToday]);

  const selectedProduction = production.byArea?.[dataset?.key];
  const getEffectiveMinutesAt = (date, durationMinutes = 1) => {
    if (!selectedProduction?.output) return undefined;
    const midnight = new Date(date);
    midnight.setHours(0, 0, 0, 0);
    const first = selectedProduction.days.find((day) => Date.parse(day.dayStart) === midnight.getTime())?.productionStart;
    if (!first) return 0;
    midnight.setDate(midnight.getDate() + 1);
    const end = Math.min(Date.parse(date) + durationMinutes * 60_000, midnight.getTime());
    const output = selectedProduction.output;
    let index = 0;
    let limit = output.length;
    while (index < limit) {
      const middle = (index + limit) >>> 1;
      if (Date.parse(output[middle].windowStart) < end) index = middle + 1;
      else limit = middle;
    }
    const last = output[index - 1];
    return last && Date.parse(last.windowStart) >= Date.parse(first) ? (Date.parse(last.windowStart) - Date.parse(first)) / 60_000 + (last.windowLength ?? 1) : 0;
  };

  const coversDate = (date) => {
    const dateDay = new Date(date);
    dateDay.setHours(0, 0, 0, 0);
    return date && fetchStart && dateDay.getTime() >= Date.parse(fetchStart) && dateDay.getTime() <= day.getTime();
  };
  const counterEnd = rangeMode === 'live' ? Infinity : Date.parse(to) + 60_000;
  const days = useMemo(
    () =>
      selectedProduction?.days.map((entry) => {
        const start = Date.parse(entry.dayStart);
        const nextDay = new Date(start);
        nextDay.setDate(nextDay.getDate() + 1);
        const end = Math.min(nextDay.getTime(), counterEnd);
        const latest = selectedProduction.entries.filter((point) => Date.parse(point.windowStart) >= start && Date.parse(point.windowStart) < end).slice(-1)[0];
        return { ...entry, runningMetres: latest?.runningMetres ?? null };
      }),
    [counterEnd, selectedProduction]
  );
  const latestRunningMetres = days?.[days.length - 1]?.runningMetres ?? null;
  return { dayStart, ...selectedProduction, days, byArea: production.byArea, latestRunningMetres, loading: Boolean(enabled && dayStart && production.loading), coversDate, getEffectiveMinutesAt };
}
